import type { GameState } from '@full-elevator/sim';
import type { Graphics, Text } from 'pixi.js';
import { DEPARTURES } from '../theme.js';

/**
 * The departure board (ADR-0031): the next departure that someone is still
 * running for, and where it leaves from. A commuter's deadline is their train,
 * and in the hotel a guest's is the shuttle, breakfast closing or the wedding
 * car (ADR-0051), so the board reads it off them; nothing here decides when
 * anything goes.
 */
export interface Departure {
  readonly atMs: number;
  readonly platform: number;
  /** Those running for it not yet there. */
  readonly running: number;
  /** What the board calls it. */
  readonly word: string;
}

/** Who runs for a departure, and what the board calls it. */
const BOARD_WORD: Readonly<Record<string, string>> = {
  commuter: 'TRAIN',
  shuttleGuest: 'SHUTTLE',
  breakfastGuest: 'BREAKFAST',
  newlywed: 'WEDDING',
};

/** Every departure still awaited, the soonest on each floor, soonest first. */
export function departures(state: GameState): Departure[] {
  const byFloor = new Map<number, Departure>();
  for (const p of state.passengers) {
    const word = BOARD_WORD[p.archetype];
    if (!word || p.deadlineMs === null) continue;
    if (p.state === 'delivered' || p.state === 'abandoned' || p.deadlineMs < state.elapsedMs) {
      continue;
    }
    const known = byFloor.get(p.destination);
    if (!known || p.deadlineMs < known.atMs) {
      byFloor.set(p.destination, { atMs: p.deadlineMs, platform: p.destination, running: 1, word });
    } else if (p.deadlineMs === known.atMs) {
      byFloor.set(p.destination, { ...known, running: known.running + 1 });
    }
  }
  return [...byFloor.values()].sort((a, b) => a.atMs - b.atMs);
}

/** The soonest departure anyone is still running for. */
export function nextDeparture(state: GameState): Departure | null {
  return departures(state)[0] ?? null;
}

/** Minutes and seconds, as a station board shows them. */
export function boardTime(ms: number): string {
  const seconds = Math.ceil(Math.max(0, ms) / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The board itself, hung from the platform's ceiling at `x`, `top`: the time
 * left, and in the last ten seconds a blink, unless the player asked for stillness.
 */
export function paintBoard(
  g: Graphics,
  label: Text,
  x: number,
  top: number,
  leftMs: number,
  ms: number,
  still: boolean,
  word = 'TRAIN',
): void {
  const w = word.length > 6 ? 96 : 78;
  const h = 20;
  g.moveTo(x + 14, top - 5)
    .lineTo(x + 14, top)
    .stroke({ width: 1.5, color: DEPARTURES.rim });
  g.moveTo(x + w - 14, top - 5)
    .lineTo(x + w - 14, top)
    .stroke({ width: 1.5, color: DEPARTURES.rim });
  g.roundRect(x, top, w, h, 4)
    .fill({ color: DEPARTURES.face })
    .stroke({ width: 1.5, color: DEPARTURES.rim });
  const text = `${word} ${boardTime(leftMs)}`;
  if (label.text !== text) label.text = text;
  label.position.set(x + w / 2, top + h / 2);
  const urgent = leftMs <= 10_000;
  label.alpha = urgent && !still && Math.floor(ms / 400) % 2 === 1 ? 0.35 : 1;
}
